import { useCallback, useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Overlay from 'ol/Overlay'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Style, Stroke, Fill } from 'ol/style'
import { GeoJSON } from 'ol/format'
import useMap from 'hooks/use-map'
import useMapCoordinate from 'hooks/use-click-coordinate'
import { swathInfoReq, swathInfoClr } from 'actions/map'
import { get, isNil } from 'es-toolkit/compat'
import union from '@turf/union'
import SwathInfoView from './swath-info-view'
import { DraggableCore } from 'react-draggable'
import { trackEvent } from 'api/usage-tracking'

import PopupInternal from 'components/popup/popup-internal'
import { colors } from 'components/foundation'
import { TEST_IDS } from 'test-ids'

export default function SwathInfo() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const map = useMap()
  const coordinate = useMapCoordinate()
  const [overlay, setOverlay] = useState(null)
  // Tracks whether the popup should be shown, independent of `data`: closing
  // the popup also clears `data` back to the loading sentinel (c.UNDEFINED),
  // which is otherwise indistinguishable from "a new inspection just started."
  const [visible, setVisible] = useState(false)
  const nodeRef = useRef(null)
  const [lastHullLayer, setLastHullLayer] = useState(null)
  // Accumulated in a ref (not React state) and applied straight to the DOM:
  // react-draggable's own uncontrolled state (`this.state.x/y`) can read stale
  // values under React 18/19's automatic batching of native-event-driven
  // setState calls, silently dropping deltas during fast mouse movement.
  const dragOffsetRef = useRef({ x: 0, y: 0 })

  const handlePopupDrag = useCallback((e, data) => {
    const offset = dragOffsetRef.current
    offset.x += data.deltaX
    offset.y += data.deltaY
    if (nodeRef.current) {
      nodeRef.current.style.transform = `translate(${offset.x}px, ${offset.y}px)`
    }
  }, [])

  const data = useSelector((state) => get(state, 'map.swathInfoRes'))

  useEffect(() => {
    // Tracking event: only once per session
    if (!sessionStorage.getItem('umami_swath_inspect_fired')) {
      trackEvent('swath-inspection-tool')
      sessionStorage.setItem('umami_swath_inspect_fired', '1')
    }
    if (coordinate && coordinate[0] !== 0) {
      setVisible(true)
      dispatch(swathInfoClr())
      dispatch(swathInfoReq(coordinate))

      // Reset any leftover drag offset from a previous inspection so the
      // popup re-anchors exactly at the new click location.
      dragOffsetRef.current = { x: 0, y: 0 }
      if (nodeRef.current) {
        nodeRef.current.style.transform = ''
      }
    }
  }, [coordinate])

  const onClose = useCallback(() => {
    setVisible(false)
    if (overlay != null) {
      overlay.setPosition(undefined)
    }
    if (map && lastHullLayer) {
      map.removeLayer(lastHullLayer)
      setLastHullLayer(null)
    }
    dispatch(swathInfoClr())
  }, [overlay, map, lastHullLayer])

  useEffect(() => {
    if (map) {
      const currentOverlays = [...map.getOverlays().getArray()]

      // Clean up any stale overlays with the same ID
      currentOverlays.forEach((o) => {
        if (o !== overlay && 'id' in o && o.id === TEST_IDS.swathInfoPopup) {
          map.removeOverlay(o)
        }
      })

      if (visible && !isNil(data)) {
        const el = document.getElementById(TEST_IDS.swathInfoPopup)

        if (overlay == null && el) {
          const _overlay = new Overlay({
            element: el,
            position: coordinate,
            offset: [-200, -20],
            autoPan: false,
          })
          _overlay.id = TEST_IDS.swathInfoPopup
          setOverlay(_overlay)
        } else if (overlay != null) {
          overlay.setPosition(coordinate)
        }
      }
    }
  }, [map, coordinate, overlay, data, visible])

  useEffect(() => {
    if (map && overlay) {
      map.addOverlay(overlay)
    }
    return () => {
      if (map && overlay) {
        map.removeOverlay(overlay)
      }
    }
  }, [map, overlay])

  useEffect(() => {
    // Clean up hull layer when component unmounts
    return () => {
      if (map && lastHullLayer) {
        map.removeLayer(lastHullLayer)
      }
    }
  }, [map, lastHullLayer])

  useEffect(() => {
    // Logic for hull calculation and drawing.
    // Intentionally excludes `overlay` from deps — overlay positioning is
    // handled in a separate effect so that setting the overlay state does not
    // cause the hull to be removed and re-drawn unnecessarily.
    if (map && lastHullLayer) {
      map.removeLayer(lastHullLayer)
      setLastHullLayer(null)
    }

    if (map && data && data.features && data.features.length > 0) {
      try {
        const reader = new GeoJSON()

        // Read raw features (EPSG:3857) into OpenLayers features
        // This validates them and brings them into the map's projection space
        const rawFeatures = reader.readFeatures(data, {
          dataProjection: 'EPSG:3857',
          featureProjection: 'EPSG:3857',
        })

        if (rawFeatures.length > 0) {
          // Convert to WGS84 (EPSG:4326) for Turf operations
          // Turf expects numeric coordinates in lat/lon range
          const geoJsonWriter = new GeoJSON()
          const wgs84GeoJson = geoJsonWriter.writeFeaturesObject(rawFeatures, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:4326',
          })

          // Calculate hull using Turf (on WGS84 data). This isn't really a hull, it merges the polygons together,
          // but it serves a similar visual purpose for swaths. Should we want to support points, we would need
          // to switch to a true convex hull calculation instead.
          let merged = null
          try {
            merged = union(wgs84GeoJson)
          } catch (err) {
            console.warn('Union calculation failed for feature collection', err)
          }

          // Render result (transforming back to 3857)
          const finalFeatures = []

          if (merged) {
            const hullFeature = reader.readFeature(merged, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857',
            })
            if (hullFeature) finalFeatures.push(hullFeature)
          }

          if (finalFeatures.length === 0) {
            // Fallback: if union failed or produced an unreadable geometry, render raw swath features
            finalFeatures.push(...rawFeatures)
          }

          if (finalFeatures.length > 0) {
            const source = new VectorSource({
              features: finalFeatures,
            })

            const layer = new VectorLayer({
              source: source,
              style: new Style({
                stroke: new Stroke({
                  color: colors.brand,
                  width: 2,
                }),
                fill: new Fill({
                  color: colors.brandTint,
                }),
              }),
              zIndex: 999,
            })
            layer.set('id', 'swath-hull')
            map.addLayer(layer)
            setLastHullLayer(layer)
          }
        }
      } catch (e) {
        console.error('Error computing swath hull', e)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, data])

  return (
    <div>
      <div id={TEST_IDS.swathInfoPopup} data-testid={TEST_IDS.swathInfoPopup}>
        {overlay != null ? (
          <DraggableCore
            handle="h1"
            nodeRef={nodeRef}
            offsetParent={document.body}
            onDrag={handlePopupDrag}
          >
            {/* This div is the single positioning/drag layer: react-draggable
                translates it directly, so PopupInternal below must render in
                normal flow (position: static) rather than positioning itself,
                or the two would fight over where the popup actually sits. */}
            <div ref={nodeRef} style={{ position: 'relative' }}>
              <PopupInternal
                key={'swath-info-popup'}
                onClose={onClose}
                title={t('swath_inspection')}
                pixel={{ x: 5, y: 20 }}
                hideArrow={true}
                closeOnOutsideClick={false}
                style={{ position: 'static' }}
              >
                <SwathInfoView />
              </PopupInternal>
            </div>
          </DraggableCore>
        ) : null}
      </div>
    </div>
  )
}
